// src/molecules/FavoritesMenu.tsx
import { useState as useState2, useCallback as useCallback2, useMemo as useMemo2, useEffect } from "react";
import { Tooltip, Button as Button2, DropdownShell } from "@design-systems/canvas-ui";
import Icon2, { StarIcon } from "@design-systems/react-icons";

// src/graphql/FavoriteQueries.ts
var GET_FAVORITE_TYPES_FOR_CURRENT_USER = `
  query getFavoriteTypes {
    favoriteTypesByUser
  }
`;
var ADD_FAVORITE = `
  mutation addFavorite($id: String!, $type: String!) {
    addFavorite(resourceId: $id, resourceType: $type) {
      id
      resourceId
      resourceType
    }
  }
`;
var DELETE_FAVORITE = `
  mutation deleteFavorite($id: String!, $type: String!) {
    deleteFavorite(resourceId: $id, resourceType: $type) {
      id
    }
  }
`;
var IS_ENTITY_FAVORITE = `
  query isEntityFavorite($id: String!, $type: String!) {
    favorite(resourceId: $id, resourceType: $type) {
      id
      resourceId
      resourceType
    }
  }
`;
var GET_FAVORITES_BY_TYPES = `
  query getFavoritesByTypes($types: [String!]!) {
    favorites(types: $types, page: 0, size: 100, sort: { field: "createdAt", direction: DESC }) {
      content {
        id
        resourceId
        resourceType
        resource {
          ... on Artwork {
            id
            description
            primaryAttachment {
              id
            }
          }
          ... on Color {
            id
            description
            primaryAttachment {
              id
            }
          }
          ... on Combination {
            id
            description
            primaryAttachment {
              id
            }
          }
          ... on Fabric {
            id
            description
            primaryAttachment {
              id
            }
          }
          ... on FabricSpecification {
            id
            name
            description
            primaryAttachment {
              id
            }
          }
          ... on BaseMaterial {
            id
            name
            description
            primaryAttachment {
              id
            }
          }
          ... on Palette {
            id
            description
          }
          ... on Trim {
            id
            description
            primaryAttachment {
              id
            }
          }
          ... on Wash {
            id
            description
            primaryAttachment {
              id
            }
          }
          ... on Product {
            id
            description
            primaryAttachmentResourceId
          }
          ... on Workspace {
            id
            description
          }
          ... on ExternalTeam {
            id
            teamName
            teamType
          }
          ... on InternalTeam {
            id
            teamName
            teamType
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        pageNumber
        pageSize
        totalElements
        totalPages
      }
    }
  }
`;

// src/atoms/FavoriteMenuContent.tsx
import { useState, useMemo, useCallback } from "react";
import {
  Button,
  Tabs,
  Grid as Grid2,
  Heading,
  Separator,
  List,
  Caption,
  Anchor
} from "@design-systems/canvas-ui";
import Icon, { RefreshIcon, ErrorFillIcon } from "@design-systems/react-icons";

// src/atoms/FavoritesLoadingIndicator.tsx
import { Grid, Spinner } from "@design-systems/canvas-ui";
import { jsx, jsxs } from "react/jsx-runtime";
var FavoritesLoadingIndicator = ({ loadingText }) => {
  return /* @__PURE__ */ jsxs(Grid.Container, { align: "center", justify: "center", direction: "column", children: [
    /* @__PURE__ */ jsx(Grid.Item, { children: /* @__PURE__ */ jsx(Spinner, { size: "lg" }) }),
    /* @__PURE__ */ jsx(Grid.Item, { children: loadingText })
  ] });
};
var FavoritesLoadingIndicator_default = FavoritesLoadingIndicator;

// src/atoms/FavoriteMenuContent.tsx
import {
  IsolatedComponent,
  useGraphQL,
  ReactRouterCanvasAnchor
} from "@plm-core/utils";
import styles from "./FavoriteMenuContent.module-XIJYEFZL.module.css";
import { Fragment, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var FavoritesContent = ({
  favoritesEntityConfigList,
  toggleDropdown,
  tabsWithData,
  loadingTabsWithData,
  refetchFavoritesByType,
  xApiKey
}) => {
  const tabTitles = useMemo(
    () => favoritesEntityConfigList.map((config) => ({
      favoriteType: config.favoriteEntity,
      title: config.tabTitle
    })).filter((tab) => tabsWithData.includes(tab.favoriteType)),
    [favoritesEntityConfigList, tabsWithData]
  );
  const [activeTab, setActiveTab] = useState(tabTitles[0].favoriteType);
  const activeEntityConfig = useMemo(
    () => favoritesEntityConfigList.find(
      (config) => config.favoriteEntity === activeTab
    ),
    [favoritesEntityConfigList, activeTab]
  );
  const {
    data: favoriteEntityData,
    loading: favoriteEntityLoading,
    error: favoriteEntityError,
    refetch: refetchFavoriteEntity
  } = useGraphQL(
    activeEntityConfig?.query || GET_FAVORITES_BY_TYPES,
    {
      variables: activeEntityConfig?.customQueryVariables ?? {
        types: [activeTab]
      },
      headers: xApiKey ? { "X-Api-Key": xApiKey } : void 0,
      // or no cache?
      fetchPolicy: "cache-and-network",
      skip: !activeEntityConfig
    }
  );
  const queriedContent = activeEntityConfig?.getDataPath?.(favoriteEntityData) ?? favoriteEntityData?.favorites?.content;
  const content = Array.isArray(queriedContent) ? queriedContent : [];
  const hasContent = content.length > 0;
  const handleRefreshClick = useCallback(async () => {
    await refetchFavoritesByType();
    await refetchFavoriteEntity();
  }, [refetchFavoritesByType, refetchFavoriteEntity]);
  return /* @__PURE__ */ jsxs2(Grid2.Container, { children: [
    /* @__PURE__ */ jsxs2(Grid2.Container, { className: "hc8-pt-2x hc8-ph-4x", children: [
      /* @__PURE__ */ jsx2(Grid2.Item, { size: { xs: 12 }, children: /* @__PURE__ */ jsxs2(Grid2.Container, { children: [
        /* @__PURE__ */ jsx2(
          Grid2.Item,
          {
            size: {
              xs: 11
            },
            children: /* @__PURE__ */ jsx2(Heading, { "aria-label": "Favorites header", size: 4, children: "Favorites" })
          }
        ),
        /* @__PURE__ */ jsx2(
          Grid2.Item,
          {
            size: {
              xs: 1
            },
            children: /* @__PURE__ */ jsx2(
              Button,
              {
                variant: "ghost-neutral",
                "aria-label": "Refresh",
                isIconOnly: true,
                onClick: handleRefreshClick,
                children: /* @__PURE__ */ jsx2(Icon, { svg: RefreshIcon })
              }
            )
          }
        )
      ] }) }),
      !loadingTabsWithData && /* @__PURE__ */ jsx2(Grid2.Item, { size: { xs: 12 }, children: /* @__PURE__ */ jsx2(IsolatedComponent, { children: /* @__PURE__ */ jsx2(Tabs, { activeTab, onTabSelect: setActiveTab, children: tabTitles.map((title) => /* @__PURE__ */ jsx2(Tabs.Item, { name: title.favoriteType, children: title.title }, title.favoriteType)) }) }) }),
      loadingTabsWithData && /* @__PURE__ */ jsx2(FavoritesLoadingIndicator_default, { loadingText: "Loading Favorites Content..." })
    ] }),
    !loadingTabsWithData && tabTitles.length > 0 && /* @__PURE__ */ jsxs2(Fragment, { children: [
      /* @__PURE__ */ jsx2(Separator, {}),
      /* @__PURE__ */ jsxs2(
        Grid2.Item,
        {
          size: { xs: 12 },
          className: styles.customDropdownScrollArea,
          children: [
            favoriteEntityLoading && /* @__PURE__ */ jsx2(
              FavoritesLoadingIndicator_default,
              {
                loadingText: `Loading ${activeEntityConfig?.tabTitle || "Favorites"}...`
              }
            ),
            !favoriteEntityLoading && favoriteEntityError && !hasContent && /* @__PURE__ */ jsx2(
              Grid2.Container,
              {
                className: "hc8-wth-100",
                direction: "column",
                align: "center",
                justify: "center",
                children: /* @__PURE__ */ jsx2(Grid2.Item, { size: { xs: 12 }, children: /* @__PURE__ */ jsx2(Grid2.Container, { align: "center", justify: "center", children: /* @__PURE__ */ jsx2(
                  Grid2.Item,
                  {
                    size: { xs: 12 },
                    align: "center",
                    justify: "center",
                    children: /* @__PURE__ */ jsxs2(Caption, { "aria-label": "Error loading favorites", children: [
                      /* @__PURE__ */ jsx2(
                        Icon,
                        {
                          svg: ErrorFillIcon,
                          color: "var(--color-background-inactive-contrast)"
                        }
                      ),
                      "Couldn't load favorites.\xA0",
                      /* @__PURE__ */ jsx2(
                        Anchor,
                        {
                          "aria-label": "Click to retry loading favorites",
                          onClick: handleRefreshClick,
                          children: "Click to retry"
                        }
                      )
                    ] })
                  }
                ) }) })
              }
            ),
            !favoriteEntityLoading && hasContent && /* @__PURE__ */ jsx2(List, { children: content.map((item, index) => /* @__PURE__ */ jsx2(
              List.Item,
              {
                as: ReactRouterCanvasAnchor,
                onClick: toggleDropdown,
                to: activeEntityConfig?.getFavoriteUrl(
                  item,
                  index,
                  favoriteEntityData
                ) || "#",
                children: activeEntityConfig?.getRowItemData(
                  item,
                  index,
                  favoriteEntityData
                )
              },
              index
            )) })
          ]
        }
      )
    ] })
  ] });
};
var FavoriteMenuContent_default = FavoritesContent;

// src/events/FavoritesEvents.ts
var FavoritesEventsClass = class {
  constructor() {
    this.listeners = /* @__PURE__ */ new Set();
    this.broadcastChannel = null;
    if (typeof window !== "undefined" && "BroadcastChannel" in window) {
      this.broadcastChannel = new BroadcastChannel("@plm-core/favorites-events");
      this.broadcastChannel.onmessage = (event) => {
        this.listeners.forEach((listener) => {
          try {
            listener(event.data);
          } catch (error) {
            console.error("Error in favorites event listener:", error);
          }
        });
      };
    } else {
      console.warn(
        "BroadcastChannel not available, favorites events will only work within the same bundle"
      );
    }
  }
  /**
   * Subscribe to favorites events
   * @returns Unsubscribe function
   */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  /**
   * Emit a favorites event to all subscribers across all contexts
   */
  emit(event) {
    if (this.broadcastChannel) {
      try {
        this.broadcastChannel.postMessage(event);
      } catch (error) {
        console.error("Error broadcasting favorites event:", error);
      }
    }
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        console.error("Error in favorites event listener:", error);
      }
    });
  }
  /**
   * Get the number of active subscribers (useful for debugging)
   */
  getListenerCount() {
    return this.listeners.size;
  }
  /**
   * Clean up the BroadcastChannel (call when the app is unmounting)
   */
  close() {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
  }
};
var FavoritesEvents = new FavoritesEventsClass();

// src/molecules/FavoritesMenu.tsx
import { useGraphQL as useGraphQL2 } from "@plm-core/utils";
import styles2 from "./FavoritesMenu.module-XZ5N22U4.module.css";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var FavoritesMenu = ({ favoritesEntityConfigList = [], xApiKey }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState2(false);
  const toggleDropdown = useCallback2(() => {
    setIsDropdownOpen(!isDropdownOpen);
  }, [isDropdownOpen]);
  const favoriteSupportedEntities = useMemo2(
    () => favoritesEntityConfigList.map((config) => config.favoriteEntity),
    [favoritesEntityConfigList]
  );
  const typesToQuery = useMemo2(() => {
    const groupedEntities = favoritesEntityConfigList.filter((config) => config.groupedEntities).flatMap((config) => config.groupedEntities || []);
    return [...favoriteSupportedEntities, ...groupedEntities];
  }, [favoritesEntityConfigList, favoriteSupportedEntities]);
  const {
    data: favoriteTypesData,
    loading: favoriteTypesDataLoading,
    refetch: refetchFavoritesByType
  } = useGraphQL2(
    GET_FAVORITE_TYPES_FOR_CURRENT_USER,
    {
      variables: { types: typesToQuery },
      fetchPolicy: "network-only",
      skip: typesToQuery.length === 0,
      headers: xApiKey ? { "X-Api-Key": xApiKey } : void 0
    }
  );
  useEffect(() => {
    const unsubscribe = FavoritesEvents.subscribe((event) => {
      if (event.type === "FAVORITE_ADDED" || event.type === "FAVORITE_REMOVED") {
        refetchFavoritesByType({ types: typesToQuery });
      }
    });
    return unsubscribe;
  }, [refetchFavoritesByType, typesToQuery]);
  const favoriteTypesForCurrentUser = useMemo2(
    () => favoriteTypesData?.favoriteTypesByUser || [],
    [favoriteTypesData]
  );
  const configuredFavorites = useMemo2(
    () => favoritesEntityConfigList.map((config) => {
      const hasData = favoriteTypesForCurrentUser.includes(
        config.favoriteEntity
      );
      const hasGroupedData = config.groupedEntities?.some(
        (groupedEntity) => favoriteTypesForCurrentUser.includes(groupedEntity)
      );
      return hasData || hasGroupedData ? config.favoriteEntity : null;
    }).filter((favoriteType) => favoriteType !== null),
    [favoritesEntityConfigList, favoriteTypesForCurrentUser]
  );
  if (favoriteTypesDataLoading || configuredFavorites.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxs3(
    DropdownShell,
    {
      "aria-label": "Favorites menu container",
      onClose: toggleDropdown,
      isOpen: isDropdownOpen,
      location: "bottom-right",
      children: [
        /* @__PURE__ */ jsx3(
          Tooltip,
          {
            "aria-label": "Favorites menu tooltip",
            content: "Favorites",
            location: "bottom-right",
            children: /* @__PURE__ */ jsx3(DropdownShell.Toggle, { children: /* @__PURE__ */ jsx3(
              Button2,
              {
                onClick: toggleDropdown,
                variant: "ghost-neutral",
                "aria-label": "Favorites button",
                isIconOnly: true,
                children: /* @__PURE__ */ jsx3(Icon2, { svg: StarIcon })
              }
            ) })
          }
        ),
        /* @__PURE__ */ jsx3(DropdownShell.Content, { className: styles2.customDropdownContent, children: /* @__PURE__ */ jsx3(
          FavoriteMenuContent_default,
          {
            favoritesEntityConfigList,
            toggleDropdown,
            tabsWithData: configuredFavorites,
            loadingTabsWithData: favoriteTypesDataLoading,
            refetchFavoritesByType,
            xApiKey
          }
        ) })
      ]
    }
  );
};
var FavoritesMenu_default = FavoritesMenu;

// src/atoms/FavoriteItem.tsx
import { Grid as Grid3, Caption as Caption2, Image } from "@design-systems/canvas-ui";
import Icon3 from "@design-systems/react-icons";
import styles3 from "./FavoriteItem.module-EDVLCMU2.module.css";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var FavoriteItem = ({
  canvasSvgIcon,
  imageUrl,
  bodyText,
  id,
  iconColor,
  variant = "visual"
}) => {
  const imageClassName = variant === "dense" ? styles3.customDenseImageSize : styles3.customImageSize;
  const imageSize = variant === "dense" ? 1 : 2;
  const contentSize = variant === "dense" ? 11 : 10;
  const captionClassName = variant === "dense" ? `hc8-fs-100 hc8-ml-4x ${styles3.textTruncate}` : `hc8-fs-100 ${styles3.textTruncate}`;
  return /* @__PURE__ */ jsx4(
    Grid3.Item,
    {
      className: "hc8-ph-3x",
      size: {
        xs: 12
      },
      children: /* @__PURE__ */ jsxs4(Grid3.Container, { align: "center", children: [
        /* @__PURE__ */ jsx4(Grid3.Item, { size: { xs: imageSize }, children: imageUrl ? /* @__PURE__ */ jsx4(
          Image,
          {
            className: imageClassName,
            height: "68px",
            width: "68px",
            src: imageUrl,
            alt: `image-for-${id}`
          }
        ) : /* @__PURE__ */ jsx4(
          Icon3,
          {
            className: imageClassName,
            svg: canvasSvgIcon,
            color: iconColor
          }
        ) }),
        /* @__PURE__ */ jsx4(Grid3.Item, { size: { xs: contentSize }, children: /* @__PURE__ */ jsx4(Caption2, { className: captionClassName, below: id, children: /* @__PURE__ */ jsx4("strong", { children: bodyText }) }) })
      ] })
    }
  );
};
var FavoriteItem_default = FavoriteItem;

// src/atoms/FavoriteButton.tsx
import { useCallback as useCallback3, useMemo as useMemo3 } from "react";
import Icon4, { StarIcon as StarIcon2, StarFillIcon } from "@design-systems/react-icons";
import { Button as Button3, Tooltip as Tooltip2 } from "@design-systems/canvas-ui";
import {
  useResponsiveLayout,
  useGraphQL as useGraphQL3,
  useGraphQLMutation
} from "@plm-core/utils";
import { Fragment as Fragment2, jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var FavoriteButton = ({
  entityId,
  entityType,
  queryForFavoriteEntityCheck,
  variablesForFavoriteEntityCheck,
  getDataPathForIsFavoriteQuery,
  queriesToRefetchOnUpdate,
  label,
  variant = "ghost-neutral",
  isDisabled,
  xApiKey,
  ...buttonProps
}) => {
  const isFavoriteQuery = useMemo3(
    () => queryForFavoriteEntityCheck ?? IS_ENTITY_FAVORITE,
    [queryForFavoriteEntityCheck]
  );
  const isFavoriteQueryVariables = useMemo3(
    () => variablesForFavoriteEntityCheck ?? { id: entityId, type: entityType },
    [variablesForFavoriteEntityCheck, entityId, entityType]
  );
  const {
    data: isEntityFavoriteData,
    loading: isEntityFavoriteLoading,
    error: isEntityFavoriteError,
    refetch: refetchIsEntityFavorite
  } = useGraphQL3(isFavoriteQuery, {
    variables: isFavoriteQueryVariables,
    skip: !entityId,
    fetchPolicy: "cache-and-network",
    headers: xApiKey ? { "X-Api-Key": xApiKey } : void 0
  });
  const isCurrentlyFavorite = useMemo3(
    () => getDataPathForIsFavoriteQuery?.(isEntityFavoriteData) ?? !!isEntityFavoriteData?.favorite?.resourceId,
    [isEntityFavoriteData, getDataPathForIsFavoriteQuery]
  );
  const [
    addFavorite,
    { loading: addFavoriteLoading, error: addFavoriteError }
  ] = useGraphQLMutation(ADD_FAVORITE);
  const [
    deleteFavorite,
    { loading: deleteFavoriteLoading, error: deleteFavoriteError }
  ] = useGraphQLMutation(DELETE_FAVORITE);
  const { canvasScreenSize } = useResponsiveLayout();
  const isMediumScreen = canvasScreenSize === "md";
  const isSmallScreen = canvasScreenSize === "sm";
  const handleClick = useCallback3(
    async (event) => {
      event.stopPropagation();
      event.preventDefault();
      if (isCurrentlyFavorite) {
        await deleteFavorite({
          variables: {
            id: entityId,
            type: entityType
          },
          headers: xApiKey ? { "X-Api-Key": xApiKey } : void 0
        });
        await refetchIsEntityFavorite({ variables: isFavoriteQueryVariables });
        FavoritesEvents.emit({
          type: "FAVORITE_REMOVED",
          entityId,
          entityType
        });
      } else {
        await addFavorite({
          variables: {
            id: entityId,
            type: entityType
          },
          headers: xApiKey ? { "X-Api-Key": xApiKey } : void 0
        });
        await refetchIsEntityFavorite({ variables: isFavoriteQueryVariables });
        FavoritesEvents.emit({
          type: "FAVORITE_ADDED",
          entityId,
          entityType
        });
      }
    },
    [
      isCurrentlyFavorite,
      deleteFavorite,
      entityId,
      entityType,
      xApiKey,
      refetchIsEntityFavorite,
      isFavoriteQueryVariables,
      addFavorite
    ]
  );
  const isError = isEntityFavoriteError || addFavoriteError || deleteFavoriteError;
  const isLoading = isEntityFavoriteLoading || addFavoriteLoading || deleteFavoriteLoading;
  const tooltipContent = useMemo3(() => {
    if (isError) {
      return "Unable to determine if this item is in your favorites. Please refresh and try again.";
    }
    return isCurrentlyFavorite ? "Remove from favorites" : "Add to favorites";
  }, [isCurrentlyFavorite, isError]);
  const iconToDisplay = useMemo3(() => {
    if (isError) {
      return StarFillIcon;
    }
    return isCurrentlyFavorite ? StarFillIcon : StarIcon2;
  }, [isCurrentlyFavorite, isError]);
  return /* @__PURE__ */ jsx5(Tooltip2, { content: tooltipContent, location: "bottom", children: /* @__PURE__ */ jsx5(
    Button3,
    {
      isLoading,
      isDisabled: isDisabled || isError,
      "aria-label": tooltipContent,
      variant,
      isIconOnly: !label,
      onClick: handleClick,
      ...buttonProps,
      children: isSmallScreen || isMediumScreen ? /* @__PURE__ */ jsx5(Icon4, { svg: iconToDisplay }) : /* @__PURE__ */ jsxs5(Fragment2, { children: [
        /* @__PURE__ */ jsx5(Icon4, { svg: iconToDisplay }),
        label && /* @__PURE__ */ jsxs5("span", { children: [
          " ",
          label
        ] })
      ] })
    }
  ) });
};
var FavoriteButton_default = FavoriteButton;
export {
  FavoriteButton_default as FavoriteButton,
  FavoriteItem_default as FavoriteItem,
  FavoritesMenu_default as FavoritesMenu
};
